import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import axios from "axios";
import "./App.css";
import AdminUpload from "./AdminUpload";
import Privacy from "./Privacy";
import ChatWidget from "./ChatWidget";

const API = process.env.REACT_APP_BACKEND_URL;
const T = "#2dd4bf";

const Logo = () => (
  <div className="flex items-center gap-3" data-testid="site-logo">
    <div className="w-10 h-10 rounded-lg flex items-center justify-center"
         style={{ background: T }}>
      <span className="text-black text-lg font-bold">▶</span>
    </div>
    <div>
      <div className="text-white font-bold text-lg leading-tight">Watermark Batch Pro</div>
      <div className="text-neutral-500 text-xs">Advanced Batch Video Watermarker for Mac</div>
    </div>
  </div>
);

const BuyButton = ({ label = "\uF8FF Buy Now – $39.99", testid }) => {
  const [busy, setBusy] = useState(false);
  const buy = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/payments/checkout`, {
        lookup_key: "wms_lifetime", origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      alert("Could not start checkout — please try again.");
      setBusy(false);
    }
  };
  return (
    <button data-testid={testid} onClick={buy} disabled={busy}
      className="px-6 py-3 rounded-lg font-semibold text-black transition-transform hover:scale-105 disabled:opacity-60"
      style={{ background: T }}>
      {busy ? "Loading…" : label}
    </button>
  );
};

const FEATURES = [
  ["Up to 9 Watermarks", "Add multiple image or text watermarks anywhere on your video."],
  ["Custom Timing", "Set exact start and end times for each watermark with frame precision."],
  ["Smart Cropping", "Auto-remove black bars in one click, or crop to 9:16, square, 4:3 — or draw any custom shape."],
  ["Keep Original Quality", "Any format in, crisp MP4 out — no compression, no loss."],
  ["Batch Processing", "Add multiple videos and process them all in one powerful batch."],
  ["Perfect for TikTok", "Watermark vertical videos, blur out old watermarks, and reframe widescreen shots to vertical."],
];

const POWER = [
  ["Image & Text Watermarks", "Use logos, text, or both to brand and protect your videos."],
  ["Drag & Drop Positioning", "Place watermarks exactly where you want them on screen."],
  ["Frame-Accurate Timeline", "Control when each watermark appears and disappears."],
  ["Blur Tool Included", "Draw a box to blur out anything — old watermarks, faces, plates."],
  ["Layout Presets", "Save a full watermark arrangement and re-apply it in one click."],
  ["Fast & Efficient", "Optimized for speed with Mac hardware acceleration."],
  ["Runs on Older Macs", "Works on Macs from 2010 onward. Most modern video apps demand macOS 12+ — we support macOS 10.13, so no upgrade needed."],
];

const STEPS = [
  ["1. Add Videos", "Import your videos in any format. Add multiple files to the queue."],
  ["2. Add Watermarks", "Add up to 9 watermarks. Position them, set timing, and customize."],
  ["3. Export", "Choose MP4 output and start watermarking. Done!"],
];

const FAQS = [
  ["How do I install the app?",
   "Download the DMG, double-click it, and drag Watermark Batch Pro into your Applications folder. The app is signed and notarized by Apple, so it opens right away — no security warnings, no extra steps."],
  ["What does the free trial include?",
   "Your first 5 videos render in full with no restrictions. After that, the trial renders the first 25% of each video — buy a license anytime to unlock unlimited full renders."],
  ["Where do I enter my license key?",
   "Open the app and click the license link in the top-right corner of the header. Paste your key and press Activate — the full version unlocks instantly, forever."],
  ["I paid but didn't get my license key",
   "Your key is shown on the success page right after checkout. If you missed it, email us your purchase receipt and we'll resend your key within 24 hours."],
  ["Which Macs are supported?",
   "Any Mac running macOS 10.13 High Sierra or later — including older iMacs and MacBooks. The app works completely offline and includes everything it needs (no extra installs)."],
  ["What's your refund policy?",
   "30-day money-back guarantee, no questions asked. Email support with your receipt and we'll refund you in full."],
];

function FaqItem({ q, a, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-neutral-800 overflow-hidden"
         style={{ background: "#0e1313" }}>
      <button onClick={() => setOpen(!open)} data-testid={`faq-question-${idx}`}
        className="w-full flex items-center justify-between text-left px-6 py-4 hover:bg-neutral-900 transition-colors">
        <span className="font-semibold text-sm">{q}</span>
        <span className="ml-4 text-lg" style={{ color: T }}>{open ? "−" : "+"}</span>
      </button>
      {open && (
        <div className="px-6 pb-5 text-neutral-400 text-sm leading-relaxed"
             data-testid={`faq-answer-${idx}`}>{a}</div>
      )}
    </div>
  );
}

function HeroMedia() {
  const [info, setInfo] = useState({});
  const [isPortrait, setIsPortrait] = useState(false);
  useEffect(() => {
    axios.get(`${API}/api/settings/hero-video`)
      .then((r) => setInfo(r.data)).catch(() => {});
    const mq = window.matchMedia("(orientation: portrait) and (max-width: 1023px)");
    setIsPortrait(mq.matches);
    const fn = (e) => setIsPortrait(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);
  if (isPortrait && info.portrait_uploaded) {
    return (
      <video src={`${API}/api/media/hero-video?variant=portrait`}
             autoPlay muted loop playsInline
             className="w-full max-w-[380px] mx-auto rounded-2xl border border-neutral-800 shadow-2xl"
             style={{ aspectRatio: "9/16", objectFit: "cover" }}
             data-testid="hero-video-portrait" />
    );
  }
  if (info.uploaded) {
    return (
      <video src={`${API}/api/media/hero-video`} autoPlay muted loop
             playsInline
             className="w-full rounded-xl border border-neutral-800 shadow-2xl"
             data-testid="hero-video" />
    );
  }
  return (
    <img src="/hero-screen.jpg" alt="Watermark Batch Pro app interface"
         className="w-full rounded-xl border border-neutral-800 shadow-2xl"
         data-testid="hero-screenshot" />
  );
}

function ytEmbed(url) {
  const m = (url || "").match(/(?:youtu\.be\/|v=|shorts\/|embed\/)([\w-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

function DemoVideo() {
  const [url, setUrl] = useState("");
  useEffect(() => {
    axios.get(`${API}/api/settings/youtube`)
      .then((r) => setUrl(r.data.url)).catch(() => {});
  }, []);
  const embed = ytEmbed(url);
  if (!embed) return null;
  return (
    <div className="max-w-3xl mx-auto mb-12 rounded-2xl overflow-hidden border border-neutral-800 shadow-2xl"
         data-testid="demo-video">
      <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
        <iframe src={embed} title="Watermark Batch Pro demo"
          className="absolute inset-0 w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen />
      </div>
    </div>
  );
}

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [state, setState] = useState("idle");
  const send = async () => {
    if (!form.name || !form.email || !form.message) {
      setState("missing"); return;
    }
    setState("sending");
    try {
      await axios.post(`${API}/api/support/contact`, form);
      setState("sent");
    } catch (e) { setState("error"); }
  };
  if (state === "sent") {
    return (
      <div className="text-center py-8" data-testid="contact-form-success">
        <div className="text-3xl mb-3" style={{ color: T }}>✓</div>
        <div className="font-bold">Message sent!</div>
        <p className="text-neutral-400 text-sm mt-2">
          We'll reply to {form.email} within 24 hours.</p>
      </div>
    );
  }
  const cls = "w-full p-3 rounded bg-black border border-neutral-700 text-sm mb-3";
  return (
    <div data-testid="contact-form">
      <input className={cls} placeholder="Your name" value={form.name}
             data-testid="contact-name-input"
             onChange={(e) => setForm({ ...form, name: e.target.value })} />
      <input className={cls} placeholder="Your email" type="email" value={form.email}
             data-testid="contact-email-input"
             onChange={(e) => setForm({ ...form, email: e.target.value })} />
      <textarea className={cls} rows={4} placeholder="How can we help?"
             value={form.message} data-testid="contact-message-input"
             onChange={(e) => setForm({ ...form, message: e.target.value })} />
      <button onClick={send} disabled={state === "sending"}
              data-testid="contact-send-btn"
              className="w-full py-3 rounded-lg font-semibold text-black transition-transform hover:scale-[1.02] disabled:opacity-60"
              style={{ background: T }}>
        {state === "sending" ? "Sending…" : "Send Message"}
      </button>
      {state === "missing" && (
        <div className="text-orange-400 text-xs mt-2" data-testid="contact-form-error">
          Please fill in all three fields.</div>
      )}
      {state === "error" && (
        <div className="text-red-400 text-xs mt-2" data-testid="contact-form-error">
          Could not send — please try again.</div>
      )}
    </div>
  );
}

function SupportSection() {
  return (
    <section id="support" className="max-w-6xl mx-auto px-8 py-16">
      <h2 className="text-lg font-bold text-center">
        Support & <span style={{ color: T }}>FAQ</span></h2>
      <p className="text-neutral-400 text-sm text-center mt-2 mb-10">
        Quick answers to common questions — or reach us directly, we reply within 24 hours.
      </p>
      <div className="grid md:grid-cols-[3fr_2fr] gap-10 items-start">
        <div className="space-y-3" data-testid="faq-list">
          {FAQS.map(([q, a], i) => <FaqItem key={q} q={q} a={a} idx={i} />)}
        </div>
        <div className="rounded-2xl border border-neutral-700 p-8 sticky top-24"
             style={{ background: "#0e1313" }} data-testid="support-contact-card">
          <div className="font-bold text-center">Still need help?</div>
          <p className="text-neutral-400 text-sm mt-2 mb-5 text-center">
            Send us a message — we reply by email within 24 hours.
          </p>
          <ContactForm />
          <div className="text-neutral-500 text-xs mt-4 text-center">
            Or email <a href="mailto:support@watermarkbatchpro.com"
              className="underline hover:text-white"
              data-testid="support-email-link">support@watermarkbatchpro.com</a>
            <br />30-day money-back guarantee
          </div>
        </div>
      </div>
    </section>
  );
}

function Landing() {
  const dl = () => window.open(`${API}/api/download/dmg`, "_blank");
  return (
    <div className="min-h-screen text-white" style={{ background: "#090c0c" }}>
      <header className="flex items-center justify-between px-8 py-4 border-b border-neutral-900 sticky top-0 z-10"
              style={{ background: "#0b0f0f" }} data-testid="site-header">
        <Logo />
        <nav className="hidden md:flex items-center gap-8 text-sm text-neutral-300">
          <a href="#features" className="hover:text-white">Features</a>
          <a href="#how" className="hover:text-white">How It Works</a>
          <a href="#pricing" className="hover:text-white">Pricing</a>
          <a href="#support" className="hover:text-white">Support</a>
          {localStorage.getItem("wbp_admin_key") && (
            <a href="/admin" data-testid="header-admin-link"
               className="px-3 py-1 rounded-full border font-semibold"
               style={{ color: T, borderColor: T }}>Admin</a>
          )}
        </nav>
        <div className="text-right">
          <BuyButton testid="header-buy-btn" />
          <div className="text-neutral-500 text-[10px] mt-1">macOS 10.13 or later</div>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-8 pt-20 pb-16 grid md:grid-cols-[4fr_6fr] gap-12 items-center">
        <div>
          <span className="text-xs font-semibold px-3 py-1 rounded-full border"
                style={{ color: T, borderColor: T }}>⌾ PROTECT WHAT MATTERS</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mt-6 leading-tight"
              data-testid="hero-title">
            Advanced Video<br /><span style={{ color: T }}>Watermarking.</span><br />Made Easy.
          </h1>
          <p className="text-neutral-400 mt-6 max-w-md">
            Add up to 9 watermarks anywhere on your videos, at any time.
            Crop out black bars or reframe for TikTok, keep original
            quality, and process entire folders in powerful batch.
          </p>
          <div className="flex flex-wrap items-center gap-4 mt-8">
            <BuyButton testid="hero-buy-btn" />
            <button onClick={dl} data-testid="download-trial-btn"
              className="px-6 py-3 rounded-lg font-semibold border text-white hover:bg-neutral-900"
              style={{ borderColor: "#333" }}>⭳ Download Free Trial</button>
          </div>
          <div className="text-neutral-500 text-xs mt-2">macOS 10.13 or later —
            runs great on older Macs · Signed & notarized by Apple</div>
          <div className="flex gap-6 mt-4 text-xs text-neutral-400">
            <span style={{ color: T }}>✓ <span className="text-neutral-400">Batch Processing</span></span>
            <span style={{ color: T }}>✓ <span className="text-neutral-400">Original Quality</span></span>
            <span style={{ color: T }}>✓ <span className="text-neutral-400">Any Format → MP4</span></span>
            <span style={{ color: T }}>✓ <span className="text-neutral-400">Apple Notarized</span></span>
            <span style={{ color: T }}>✓ <span className="text-neutral-400">Secure & Fast</span></span>
          </div>
        </div>
        <div>
          <HeroMedia />
        </div>
      </section>

      <section id="features" className="max-w-6xl mx-auto px-8 py-12">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map(([t, d]) => (
            <div key={t} className="rounded-xl border border-neutral-800 p-5"
                 style={{ background: "#0e1313" }}>
              <div className="font-semibold mb-2" style={{ color: T }}>{t}</div>
              <div className="text-neutral-400 text-sm">{d}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-8 py-16 grid md:grid-cols-2 gap-12 items-center">
        <div>
          <h2 className="text-lg font-bold">Powerful Features. <span style={{ color: T }}>Total Control.</span></h2>
          <div className="grid gap-y-6 mt-8">
            {POWER.map(([t, d]) => (
              <div key={t} className="flex gap-3">
                <span style={{ color: T }}>✓</span>
                <div><div className="font-semibold">{t}</div>
                  <div className="text-neutral-400 text-sm">{d}</div></div>
              </div>
            ))}
          </div>
        </div>
        <img src="/feature-frame.jpg" alt="Watermarked video frame example"
             className="rounded-xl w-full border border-neutral-800"
             data-testid="feature-image" />
      </section>

      <section id="how" className="max-w-6xl mx-auto px-8 py-12">
        <h2 className="text-lg font-bold text-center mb-10">
          Simple <span style={{ color: T }}>3-Step</span> Process</h2>
        <DemoVideo />
        <div className="grid md:grid-cols-3 gap-8">
          {STEPS.map(([t, d]) => (
            <div key={t} className="rounded-xl border border-neutral-800 p-6"
                 style={{ background: "#0e1313" }}>
              <div className="font-bold mb-2" style={{ color: T }}>{t}</div>
              <div className="text-neutral-400 text-sm">{d}</div>
            </div>
          ))}
        </div>
      </section>

      <section id="pricing" className="max-w-6xl mx-auto px-8 py-16 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <h2 className="text-lg font-bold">One-Time Purchase. <span style={{ color: T }}>Yours Forever.</span></h2>
          <ul className="mt-6 space-y-3 text-neutral-300 text-sm">
            {["No subscriptions", "Lifetime updates", "Use on your Mac",
              "24/7 Email Support"].map((x) => (
              <li key={x}><span style={{ color: T }}>✓</span> {x}</li>
            ))}
          </ul>
          <div className="mt-8 flex items-center gap-4">
            <div className="w-24 h-24 rounded-full border-4 flex flex-col items-center justify-center text-center"
                 style={{ borderColor: T }}>
              <div className="font-extrabold" style={{ color: T }}>100%</div>
              <div className="text-[8px] text-neutral-300 leading-tight">SATISFACTION<br />GUARANTEE</div>
            </div>
            <div className="text-neutral-400 text-xs">30-Day Money Back Guarantee</div>
          </div>
        </div>
        <div className="rounded-2xl border border-neutral-700 p-8 text-center"
             style={{ background: "#0e1313" }} data-testid="pricing-card">
          <div className="font-bold text-lg">Watermark Batch <span style={{ color: T }}>Pro</span></div>
          <div className="text-neutral-400 text-sm">Lifetime License</div>
          <div className="text-5xl font-extrabold my-4" style={{ color: T }}>$39.99</div>
          <div className="text-neutral-400 text-sm mb-6">One-Time Payment</div>
          <BuyButton testid="pricing-buy-btn" />
          <div className="text-neutral-500 text-xs mt-4">
            🔒 Secure checkout. Instant access.</div>
        </div>
      </section>

      <SupportSection />

      <section className="border-t border-neutral-900">
        <div className="max-w-6xl mx-auto px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">
          {[["Trusted by Creators", "Worldwide"], ["Works Offline", "No internet required"],
            ["Mac Optimized", "Built for macOS"], ["Secure & Private", "Your files stay on your Mac"]]
            .map(([t, d]) => (
            <div key={t} className="flex items-center gap-3">
              <span style={{ color: T }}>◈</span>
              <div><div className="font-semibold">{t}</div>
                <div className="text-neutral-500 text-xs">{d}</div></div>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-neutral-900 px-8 py-6 flex flex-wrap items-center justify-between gap-4 text-neutral-500 text-xs">
        <Logo />
        <span>© 2026 Watermark Batch Pro. All rights reserved.</span>
        <div className="flex gap-6">
          <a href="/privacy" className="hover:text-white" data-testid="footer-privacy-link">Privacy Policy</a>
          <a href="#pricing" className="hover:text-white">Terms of Use</a>
          <a href="#support" className="hover:text-white">Support</a>
        </div>
      </footer>
      <ChatWidget />
    </div>
  );
}

function Success() {
  const [state, setState] = useState({ status: "checking" });
  const tries = useRef(0);
  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (!sid) { setState({ status: "error" }); return; }
    const poll = async () => {
      if (tries.current++ > 10) { setState({ status: "timeout" }); return; }
      try {
        const { data } = await axios.get(`${API}/api/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", key: data.license_key }); return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState({ status: "failed" }); return;
        }
      } catch (e) { /* keep polling */ }
      setTimeout(poll, 2000);
    };
    poll();
  }, []);
  return (
    <div className="min-h-screen flex items-center justify-center text-white"
         style={{ background: "#090c0c" }}>
      <div className="rounded-2xl border border-neutral-700 p-10 max-w-lg text-center"
           style={{ background: "#0e1313" }} data-testid="success-card">
        {state.status === "checking" && <div data-testid="payment-checking">Confirming your payment…</div>}
        {state.status === "paid" && (
          <>
            <div className="text-3xl mb-3" style={{ color: T }}>✓ Payment complete!</div>
            <p className="text-neutral-400 mb-6">Here is your lifetime license key —
              open Watermark Batch Pro, click “Enter License” and paste it:</p>
            <div className="text-2xl font-mono font-bold p-4 rounded-lg border"
                 style={{ borderColor: T, color: T }} data-testid="license-key">
              {state.key}
            </div>
            <p className="text-neutral-500 text-xs mt-4">Save this key somewhere safe.
              It never expires. We've also emailed it to you.</p>
            <div className="text-left text-xs text-neutral-400 mt-6 p-4 rounded-lg border border-neutral-800"
                 style={{ background: "#0b1010" }} data-testid="install-help-note">
              <span className="font-semibold" style={{ color: T }}>Installing on your Mac:</span> open
              the downloaded DMG and drag the app to Applications. The app is signed and notarized
              by Apple, so it opens right away — no security warnings, no extra steps.
            </div>
            <a href="/" className="inline-block mt-6 text-sm underline text-neutral-300">← Back to site</a>
          </>
        )}
        {["failed", "timeout", "error"].includes(state.status) && (
          <div data-testid="payment-problem">
            <div className="text-xl mb-3 text-red-400">We couldn't confirm the payment.</div>
            <p className="text-neutral-400 text-sm">If you were charged, contact support
              with your receipt and we'll send your key right away.</p>
            <a href="/" className="inline-block mt-6 text-sm underline">← Back to site</a>
          </div>
        )}
      </div>
    </div>
  );
}

const Cancel = () => (
  <div className="min-h-screen flex items-center justify-center text-white"
       style={{ background: "#090c0c" }}>
    <div className="text-center" data-testid="cancel-card">
      <div className="text-2xl mb-2">Checkout cancelled</div>
      <p className="text-neutral-400 text-sm">No charge was made.</p>
      <a href="/" className="inline-block mt-4 underline text-sm">← Back to site</a>
    </div>
  </div>
);

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/payment/success" element={<Success />} />
        <Route path="/payment/cancel" element={<Cancel />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/admin-upload" element={<AdminUpload />} />
        <Route path="/admin" element={<AdminUpload />} />
      </Routes>
    </BrowserRouter>
  );
}
